import { RestClient } from '@/api/rest-client'
import { User } from '@/api/model/user'
import { cloneDeep } from 'lodash'
import { Company } from '@/api/model/company'
import { ref } from '@vue/composition-api'
import { getStorage } from '@/api/auth-client'

export class UserClient extends RestClient<User> {
    collectionName = 'users'
    parseData = User.fromData
    currentUser: User | null = null
    reactiveCurrentUser = ref<User>(null)

    constructor() {
        super()

        if (this.decodedAccessToken) {
            this.get(this.decodedAccessToken.id).then(user => {
                this.currentUser = user
                this.reactiveCurrentUser.value = user
            })
        }
    }

    async getCurrentUser(): Promise<User> {
        if (!this.currentUser && this.isAuthenticated) {
            this.currentUser = await this.get(this.decodedAccessToken.id)
            this.reactiveCurrentUser.value = this.currentUser
        }

        return this.currentUser
    }

    async create(data: User): Promise<User> {
        const request = cloneDeep<any>(data)

        if (data.company instanceof Company) {
            request.company = data.company.resourceIdentifier
        }

        // For branches we just send the id
        request.branches = data.branches.map(branch => branch.resourceIdentifier)

        const formData = new FormData()
        Object.keys(request).forEach(key => {
            if (!request[key]) {
                return
            }

            if (Array.isArray(request[key])) {
                const elem = request[key] as any[]
                elem.forEach(value => formData.append(`${key}[]`, value))

            } else {
                formData.append(key, request[key])
            }
        })

        try {
            await this.client.post(
                `${this.basePath}/${this.collectionName}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })
            return await this.get(data.id, true)
        } catch (error) {
            this.handleError(error)
        }
    }

    async update(id: string, data: User): Promise<User> {
        const request = cloneDeep<any>(data)

        if (data.company instanceof Company) {
            request.company = data.company.resourceIdentifier
        }

        // For branches we just send the id
        request.branches = data.branches.map(branch => branch.id)

        const formData = new FormData()
        Object.keys(request).forEach(key => {
            if (key !== 'active' && key !== 'dashboardPermissions' && !request[key]) {
                return
            }

            if (Array.isArray(request[key])) {
                const elem = request[key] as any[]
                elem.forEach(value => formData.append(`${key}[]`, value))

            } else {
                formData.append(key, request[key])
            }
        })

        try {
            await this.client.post(
                `${this.basePath}/${this.collectionName}/${id}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })

            // Update dashboard permissions
            await this.client.put(
                `${this.basePath}/${this.collectionName}/${id}/update_dashboard_permissions`, request,
            )

            return await this.get(id, true)
        } catch (error) {
            this.handleError(error)
        }
    }

    async updateAvatar(id: string, avatar: File): Promise<User> {
        const formData = new FormData()
        formData.append('avatar', avatar)

        try {
            await this.client.post(
                `${this.basePath}/${this.collectionName}/${id}/update_avatar`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })

            return await this.get(id, true)
        } catch (error) {
            this.handleError(error)
        }
    }

    async loginImpersonate(userId: string): Promise<void> {
        const storage: Storage = getStorage()

        const response = await this.client.post(`${this.basePath}/${this.collectionName}/impersonate`, {
            userId,
        })

        if (storage) {
            storage.setItem('access_token', response.data.token)
            storage.setItem('refresh_token', response.data.refresh_token)
        }
    }
}
